import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Container,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getJaDisplayName } from "components/userUtil.ts";
import FilePreview from "components/FileUpload/FilePreview";
import { useDetailRegister } from "../DetailRegisterProvider";
import { updateUserDetail } from "../updateUserDetail.ts";
import { useAuthenticatedUser } from "../../../AuthProvider";
import formatPostalCode from "components/formatPostalCode";
import useCompletionDialog from "components/CompletionDialog";
import useCanShowField from "../canShowField";
import { tryReplaceToSlash } from "components/dateUtil";

export default function DetailRegisterConfirm() {
  const navigate = useNavigate();
  const { userDetail, uploadedFiles } = useDetailRegister();
  const { openCompletionDialog, renderCompletionDialog } = useCompletionDialog("/mypage");
  const user = useAuthenticatedUser();

  // エラー表示のための状態を追加
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Check a required field. If it's missing, redirect to the form.
    if (!userDetail.applicantName) {
      navigate('/mypage/detail/register', { replace: true });
    }
  }, [userDetail, navigate]);

  const handleSubmit = async () => {
    if (!confirm("申請を送信してもよろしいですか？")) {
      return;
    }

    try {
      setLoading(true);

      await updateUserDetail(
        user.uid,
        userDetail,
        uploadedFiles,
      );
      openCompletionDialog();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "会員情報の更新中にエラーが発生しました。",
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  // エラーメッセージを閉じる処理
  const handleCloseError = () => {
    setShowError(false);
  };

  const onBack = () => {
    // Set isConfirmStep to false to maintain state consistency
    navigate('/mypage/detail/register');
  };

  // 金額をフォーマットする関数
  const formatAmount = (amount: number | string, inTenThousand: boolean = false) => {
    try {
      const num = Number(amount ?? 0);
      if (isNaN(num)) return "-";
      return `${amount.toLocaleString()}${inTenThousand ? "万円" : "円"}`;
    } catch (e) {
      return "-";
    }
  };

  // Prevent rendering if state is invalid to avoid flashing empty content.
  if (!userDetail.applicantName) {
    return null;
  }

  // Use unified field visibility logic
  const canShow = useCanShowField(
    userDetail.applicantName,
    userDetail.applicantNameKana,
    userDetail.birthDate,
    userDetail.age,
    userDetail.homePostalCode,
    userDetail.homePrefecture,
    userDetail.homeCity,
    userDetail.homeAddress1,
    userDetail.homeAddress2,
    userDetail.homePhoneNumber,
    userDetail.mobileNumber,
    userDetail.companyName,
    userDetail.invoiceNumber,
    userDetail.corporateNumber,
    userDetail.postalCode,
    userDetail.prefecture,
    userDetail.city,
    userDetail.address1,
    userDetail.address2,
    userDetail.phoneNumber,
    userDetail.faxNumber,
    userDetail.industryType,
    userDetail.annualSales,
    userDetail.employeeCount,
    userDetail.taxDelayType,
    userDetail.taxDelayBalance,
    userDetail.taxDelayStatus,
    userDetail.familyStructure,
    userDetail.factoringHistory,
    userDetail.pastProviders,
    userDetail.pastProviderAmount,
    userDetail.currentProviderHistory,
    userDetail.currentProviders,
    userDetail.currentProviderAmount,
    userDetail.personalInfoConsent,
    uploadedFiles,
  );

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Typography
          component="h1"
          variant="h5"
          sx={{ mb: 2, textAlign: "center" }}
        >
          会員情報登録（詳細）確認
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          以下の内容で登録を行います。内容をご確認の上、「登録する」ボタンを押してください。
        </Typography>

        {/* エラーメッセージを表示するSnackbar */}
        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            基本情報
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("applicantName") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("applicantName")}
                  secondary={userDetail.applicantName}
                />
              </ListItem>
            )}
            {canShow("applicantNameKana") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("applicantNameKana")}
                  secondary={userDetail.applicantNameKana}
                />
              </ListItem>
            )}
            {canShow("birthDate") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("birthDate")}
                  secondary={tryReplaceToSlash(userDetail.birthDate)}
                />
              </ListItem>
            )}
            {canShow("age") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("age")}
                  secondary={`${userDetail.age}歳`}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            自宅情報
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("homePostalCode") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homePostalCode")}
                  secondary={formatPostalCode(userDetail.homePostalCode)}
                />
              </ListItem>
            )}
            {canShow("homePrefecture") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homePrefecture")}
                  secondary={userDetail.homePrefecture}
                />
              </ListItem>
            )}
            {canShow("homeCity") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homeCity")}
                  secondary={userDetail.homeCity}
                />
              </ListItem>
            )}
            {canShow("homeAddress1") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homeAddress1")}
                  secondary={userDetail.homeAddress1}
                />
              </ListItem>
            )}
            {canShow("homeAddress2") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homeAddress2")}
                  secondary={userDetail.homeAddress2}
                />
              </ListItem>
            )}
            {canShow("homePhoneNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("homePhoneNumber")}
                  secondary={userDetail.homePhoneNumber}
                />
              </ListItem>
            )}
            {canShow("mobileNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("mobileNumber")}
                  secondary={userDetail.mobileNumber}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            会社情報
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("companyName") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("companyName")}
                  secondary={userDetail.companyName}
                />
              </ListItem>
            )}
            {canShow("invoiceNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("invoiceNumber")}
                  secondary={userDetail.invoiceNumber}
                />
              </ListItem>
            )}
            {canShow("corporateNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("corporateNumber")}
                  secondary={userDetail.corporateNumber}
                />
              </ListItem>
            )}
            {canShow("postalCode") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("postalCode")}
                  secondary={formatPostalCode(userDetail.postalCode)}
                />
              </ListItem>
            )}
            {canShow("prefecture") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("prefecture")}
                  secondary={userDetail.prefecture}
                />
              </ListItem>
            )}
            {canShow("city") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("city")}
                  secondary={userDetail.city}
                />
              </ListItem>
            )}
            {canShow("address1") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("address1")}
                  secondary={userDetail.address1}
                />
              </ListItem>
            )}
            {canShow("address2") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("address2")}
                  secondary={userDetail.address2}
                />
              </ListItem>
            )}
            {canShow("phoneNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("phoneNumber")}
                  secondary={userDetail.phoneNumber}
                />
              </ListItem>
            )}
            {canShow("faxNumber") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("faxNumber")}
                  secondary={userDetail.faxNumber}
                />
              </ListItem>
            )}
            {canShow("industryType") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("industryType")}
                  secondary={userDetail.industryType}
                />
              </ListItem>
            )}
            {canShow("annualSales") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("annualSales")}
                  secondary={userDetail.annualSales + "円"}
                />
              </ListItem>
            )}
            {canShow("employeeCount") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("employeeCount")}
                  secondary={userDetail.employeeCount}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {getJaDisplayName("familyStructure")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("familyStructure") && userDetail.familyStructure
              .map((member, idx) => (
                <ListItem key={idx} alignItems="flex-start">
                  <ListItemText
                    primary={`${getJaDisplayName("relationship")}: ${member.relationship} / ${getJaDisplayName("fullName")}: ${member.fullName}`}
                    secondary={`${getJaDisplayName("age")}: ${member.age} / ${getJaDisplayName("contactInfo")}: ${member.contactInfo}`}
                  />
                </ListItem>
              ))}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            ファクタリング情報
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("factoringHistory") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("factoringHistory")}
                  secondary={userDetail.factoringHistory}
                />
              </ListItem>
            )}
            {canShow("pastProviders") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("pastProviders")}
                  secondary={userDetail.pastProviders}
                />
              </ListItem>
            )}
            {canShow("pastProviderAmount") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("pastProviderAmount")}
                  secondary={formatAmount(userDetail.pastProviderAmount, true)}
                />
              </ListItem>
            )}
            {canShow("currentProviderHistory") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("currentProviderHistory")}
                  secondary={userDetail.currentProviderHistory}
                />
              </ListItem>
            )}
            {canShow("currentProviders") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("currentProviders")}
                  secondary={userDetail.currentProviders}
                />
              </ListItem>
            )}
            {canShow("currentProviderAmount") && (
              <ListItem>
                <ListItemText
                  primary={getJaDisplayName("currentProviderAmount")}
                  secondary={formatAmount(userDetail.currentProviderAmount, true)}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {getJaDisplayName("personalInfoConsent")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {canShow("personalInfoConsent") && (
              <ListItem>
                <ListItemText
                  primary={userDetail.personalInfoConsent ? "同意済み" : "未同意"}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        {canShow("fileUpload") && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              添付ファイル
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FilePreview title="" files={uploadedFiles} />
          </Paper>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            mb: 2,
          }}
        >
          <Button onClick={onBack} variant="outlined">
            戻る
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            登録する
          </Button>
        </Box>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {renderCompletionDialog()}
    </Container>
  );
}
